import { useLocation, useNavigate } from 'react-router-dom'
import { useApp } from '../store/AppContext'
import { useNotifications } from '../lib/notifications'
import { Avatar } from './Avatar'
import { BrandLogo } from './BrandLogo'

type NavigationState = {
  returnTo?: string
  returnLabel?: string
}

const ALEX_MAIN_ROUTES = ['/home', '/today', '/calendar', '/chats', '/more']
const MARK_MAIN_ROUTES = ['/home', '/calendar', '/team', '/chats', '/more']
const COORDINATOR_MAIN_ROUTES = ['/home', '/calendar', '/team', '/chats', '/more']

export function TopBar({ title, subtitle }: { title: string; subtitle?: string }) {
  const { currentUser, totalUnreadCount } = useApp()
  const { unreadCount } = useNotifications()
  const navigate = useNavigate()
  const location = useLocation()
  const navState = (location.state ?? {}) as NavigationState
  const isAlex = currentUser.id === 'u-alex'
  const isMark = currentUser.id === 'u-mark'
  const isCoordinator = currentUser.role === 'coordinator'
  const isLeoSupportWorker = currentUser.id === 'u-casey'
  const isAdultSupportWorker = currentUser.role === 'support_worker' && !isLeoSupportWorker
  const adultStyle = isAlex || isMark || isCoordinator || isAdultSupportWorker
  const queryFromLeo = !adultStyle && new URLSearchParams(location.search).get('from') === 'leo-workspace'
  const alliedHealthReturn = currentUser.role === 'allied_health' && location.pathname !== '/home'
    ? { returnTo: '/home', returnLabel: 'Allied Health Home' }
    : undefined
  const alexNeedsBack = isAlex && !ALEX_MAIN_ROUTES.includes(location.pathname)
  const markNeedsBack = isMark && !MARK_MAIN_ROUTES.includes(location.pathname)
  const coordinatorNeedsBack = isCoordinator && !COORDINATOR_MAIN_ROUTES.includes(location.pathname)
  const supportWorkerNeedsBack = isAdultSupportWorker && location.pathname !== '/today'
  const returnTo = navState.returnTo ?? (queryFromLeo ? '/youth-workspace' : alliedHealthReturn?.returnTo)
  const returnLabel = navState.returnLabel ?? (returnTo === '/youth-workspace' ? 'Youth workspace' : alliedHealthReturn?.returnLabel ?? 'Back')

  const handleBack = () => {
    if (returnTo) {
      navigate(returnTo)
      return
    }
    if (alexNeedsBack) {
      if (window.history.length > 1) navigate(-1)
      else navigate('/today')
      return
    }
    if (markNeedsBack) {
      // Mark has one canonical family home. Never use browser history here,
      // because older prototype routes can still exist in the history stack
      // and would otherwise resurrect the retired generic Today/Home screen.
      navigate('/home', { replace: true })
      return
    }
    if (coordinatorNeedsBack) {
      if (window.history.length > 1) navigate(-1)
      else navigate('/home')
      return
    }
    if (supportWorkerNeedsBack) {
      navigate('/today')
    }
  }

  const showBack = Boolean(returnTo || alexNeedsBack || markNeedsBack || coordinatorNeedsBack || supportWorkerNeedsBack)
  const adultContextLabel = isMark
    ? 'Family profile · Alex Turner'
    : isCoordinator
      ? 'Support coordinator · Alex Turner'
      : isAdultSupportWorker
        ? 'Support worker · Alex Turner'
        : 'Adult participant profile'

  return (
    <header className={`sticky top-0 z-20 border-b px-4 pb-3 pt-[calc(env(safe-area-inset-top)+12px)] backdrop-blur ${adultStyle ? 'border-[#dbe6ef] bg-white/95' : 'border-slate-200 bg-white/95'}`}>
      <div className="mx-auto flex max-w-md items-center justify-between gap-3">
        {showBack && (
          <button
            onClick={handleBack}
            aria-label={`Back${returnLabel ? ` to ${returnLabel}` : ''}`}
            className={`flex h-10 shrink-0 items-center gap-1.5 rounded-xl px-3 text-xs font-black transition active:scale-95 ${adultStyle ? 'bg-[#edf4ff] text-[#245a9d]' : 'bg-violet-50 text-violet-700'}`}
          >
            <span className="text-lg">←</span>
            <span className="max-w-28 truncate">Back</span>
          </button>
        )}
        <div className="min-w-0 flex-1">
          <BrandLogo variant="wordmark" className="mb-1 h-3.5 w-auto max-w-[92px]" />
          {!adultStyle && returnTo === '/youth-workspace' && <p className="mb-0.5 text-[10px] font-black uppercase tracking-wide text-violet-600">Inside youth workspace</p>}
          {adultStyle && <p className="mb-0.5 text-[9px] font-black uppercase tracking-[.12em] text-[#177f80]">{adultContextLabel}</p>}
          <h1 className={`truncate font-black tracking-[-.01em] text-slate-900 ${adultStyle ? 'text-[17px]' : 'text-lg font-bold'}`}>{title}</h1>
          {subtitle && <p className="text-xs font-semibold text-slate-500">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/notifications', returnTo ? { state: { returnTo, returnLabel } } : undefined)}
            aria-label={`${unreadCount} unread notifications`}
            className={`relative flex h-10 w-10 items-center justify-center rounded-full ${adultStyle ? 'bg-[#f3f7fb] text-[#245a9d]' : 'bg-slate-100 text-xl'}`}
          >
            🔔
            {unreadCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          <button onClick={() => navigate('/switch-user')} className="relative flex items-center gap-2">
            <Avatar user={currentUser} size={34} />
            {totalUnreadCount > 0 && (
              <span className={`absolute -left-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full text-[9px] leading-none text-white ring-2 ring-white ${adultStyle ? 'bg-[#245a9d]' : 'bg-brand-600'}`}>💬</span>
            )}
          </button>
        </div>
      </div>
    </header>
  )
}

